import React, { useState, useRef } from 'react';

// Accessible password field with a show/hide toggle. The field stays a real,
// editable <input> in both states (type toggles between password and text), so
// the value can always be edited while visible.
//
// `syncFromDom` pulls the DOM value into our controlled state when a browser
// AutoFill skips React's change event (notably Safari's strong-password
// generator). Without it, toggling Show or submitting could wipe/miss the
// autofilled value. Note: Safari binds a "keep obscured" mask to the exact
// element it autofilled, so on Safari a strong password may still display as
// bullets after Show until it's edited — an accepted tradeoff for keeping the
// field editable.
function PasswordInput({
  id,
  label,
  value,
  onChange,
  autoComplete = 'new-password',
  describedBy,
  required = true,
  minLength,
}) {
  const [show, setShow] = useState(false);
  const inputRef = useRef(null);

  const syncFromDom = () => {
    const el = inputRef.current;
    if (el && el.value !== value) onChange({ target: el });
  };

  const toggle = () => {
    syncFromDom();
    setShow((s) => !s);
  };

  return (
    <div className="auth-field">
      <label htmlFor={id}>{label}</label>
      <div className="password-wrap">
        <input
          id={id}
          ref={inputRef}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onBlur={syncFromDom}
          onAnimationStart={(e) => { if (e.animationName === 'onAutoFillStart') syncFromDom(); }}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          aria-describedby={describedBy}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={toggle}
          aria-pressed={show}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

export default PasswordInput;
